import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Aperture, Search, X, Menu } from "lucide-react";
import Logo from "./Logo";
import { API } from "../lib/api";

function SuggestionList({ items, onPick, className = "", testid }) {
  return (
    <ul
      data-testid={testid}
      className={`bg-[#0A0A0A] border border-white/10 shadow-[0_16px_48px_rgba(0,0,0,0.6)] ${className}`}
    >
      {items.map((p) => (
        <li key={p.id}>
          <button
            type="button"
            data-testid={`header-suggestion-${p.id}`}
            onClick={() => onPick(p)}
            className="w-full text-left px-5 py-3.5 flex items-baseline justify-between gap-4 hover:bg-white/5 transition-colors duration-200 group"
          >
            <span className="text-sm text-zinc-200 group-hover:text-white transition-colors duration-200">{p.title}</span>
            <span className="text-[10px] tracking-[0.2em] uppercase text-zinc-600 group-hover:text-[#D4AF37] transition-colors duration-200">{p.category}</span>
          </button>
        </li>
      ))}
    </ul>
  );
}

export default function Header() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [photos, setPhotos] = useState([]);
  const [query, setQuery] = useState("");
  const [showSug, setShowSug] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    API.get("/photos").then((r) => setPhotos(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setShowSug(false);
    setMobileOpen(false);
    setMenuOpen(false);
  }, [pathname]);

  const q = query.trim().toLowerCase();
  const suggestions = q ? photos.filter((p) => p.title.toLowerCase().includes(q)).slice(0, 6) : [];

  const openPhoto = (photo) => {
    setQuery("");
    setShowSug(false);
    setMobileOpen(false);
    navigate(`/photo/${photo.id}`);
  };

  const submit = (e) => {
    e.preventDefault();
    if (!q) return;
    setShowSug(false);
    setMobileOpen(false);
    navigate(`/gallery?q=${encodeURIComponent(query.trim())}`);
  };

  const linkCls = (to) =>
    `text-xs tracking-[0.2em] uppercase font-medium transition-colors duration-300 hover:text-white ${
      pathname === to ? "text-white" : "text-zinc-500"
    }`;

  const mobileLinkCls = (to) =>
    `block font-display text-3xl tracking-tight transition-colors duration-300 ${
      pathname === to ? "text-[#D4AF37]" : "text-white hover:text-[#D4AF37]"
    }`;

  return (
    <header
      data-testid="site-header"
      className="fixed top-0 left-0 right-0 z-50 bg-black/60 backdrop-blur-xl border-b border-white/10 shadow-[0_8px_32px_rgba(0,0,0,0.4)]"
    >
      <div className="max-w-[1600px] mx-auto px-6 md:px-12 h-20 flex items-center justify-between gap-8">
        <Link to="/" data-testid="brand-link" className="flex items-center gap-4 group shrink-0">
          <Logo size={28} />
          <span className="font-display text-lg md:text-xl tracking-[0.25em] text-white group-hover:text-[#D4AF37] transition-colors duration-300">
            TGPHOTOSHOP
          </span>
        </Link>
        <div className="flex items-center gap-6 md:gap-10 ml-auto">
          <nav className="hidden md:flex items-center gap-10" data-testid="main-nav">
            <Link to="/" className={linkCls("/")} data-testid="nav-home">Home</Link>
            <Link to="/gallery" className={linkCls("/gallery")} data-testid="nav-gallery">Gallery</Link>
            <a href="/#about" className="text-xs tracking-[0.2em] uppercase font-medium text-zinc-500 hover:text-white transition-colors duration-300" data-testid="nav-about">
              About
            </a>
          </nav>
          <form onSubmit={submit} className="relative hidden md:block" data-testid="header-search">
            <Search size={13} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => { setQuery(e.target.value); setShowSug(true); }}
              onFocus={() => setShowSug(true)}
              onBlur={() => setTimeout(() => setShowSug(false), 150)}
              onKeyDown={(e) => { if (e.key === "Escape") setShowSug(false); }}
              data-testid="header-search-input"
              placeholder="Search photographs…"
              className="w-44 lg:w-56 bg-white/5 border border-white/10 text-white text-xs pl-9 pr-3 py-2.5 focus:border-[#D4AF37] focus:outline-none focus:w-64 transition-all duration-300 placeholder:text-zinc-600"
            />
            {showSug && suggestions.length > 0 && (
              <SuggestionList
                items={suggestions}
                onPick={openPhoto}
                testid="header-search-suggestions"
                className="absolute right-0 top-full mt-3 w-72"
              />
            )}
          </form>
          <button
            type="button"
            onClick={() => { setMobileOpen((v) => !v); setMenuOpen(false); setQuery(""); }}
            data-testid="mobile-search-toggle"
            aria-label="Search"
            className="md:hidden p-2 text-zinc-400 hover:text-white transition-colors duration-300"
          >
            {mobileOpen ? <X size={18} /> : <Search size={18} />}
          </button>
          <button
            type="button"
            onClick={() => { setMenuOpen((v) => !v); setMobileOpen(false); }}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
            className="md:hidden p-2 text-zinc-400 hover:text-white transition-colors duration-300"
          >
            {menuOpen ? <X size={18} /> : <Menu size={18} />}
          </button>
          <Link
            to="/admin"
            data-testid="nav-studio"
            className="flex items-center gap-2 text-xs tracking-[0.2em] uppercase font-medium text-zinc-400 hover:text-[#D4AF37] transition-colors duration-300 shrink-0"
          >
            <Aperture size={15} />
            <span className="hidden sm:inline">Studio</span>
          </Link>
        </div>
      </div>
      {mobileOpen && (
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
        <div className="md:hidden border-t border-white/10 px-6 py-4" data-testid="mobile-search-panel">
          <form onSubmit={submit} className="relative">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none" />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              data-testid="mobile-search-input"
              placeholder="Search photographs…"
              autoFocus
              className="w-full bg-white/5 border border-white/10 text-white text-sm pl-10 pr-4 py-3 focus:border-[#D4AF37] focus:outline-none transition-colors duration-300 placeholder:text-zinc-600"
            />
          </form>
          {suggestions.length > 0 && (
            <SuggestionList
              items={suggestions}
              onPick={openPhoto}
              testid="mobile-search-suggestions"
              className="mt-3"
            />
          )}
        </div>
        </motion.div>
      )}
      {menuOpen && (
        <motion.div
          initial={{ opacity: 0, y: -16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
        >
        <nav className="md:hidden border-t border-white/10 px-6 py-10 flex flex-col gap-8" data-testid="mobile-menu">
          <Link to="/" className={mobileLinkCls("/")} data-testid="mobile-nav-home">Home</Link>
          <Link to="/gallery" className={mobileLinkCls("/gallery")} data-testid="mobile-nav-gallery">Gallery</Link>
          <a href="/#about" className="block font-display text-3xl tracking-tight text-white hover:text-[#D4AF37] transition-colors duration-300" data-testid="mobile-nav-about">
            About
          </a>
        </nav>
        </motion.div>
      )}
    </header>
  );
}
